import * as React from 'react'
import { CalendarIcon, XIcon } from 'lucide-react'

import { cn } from '@klear/ui/lib/utils'
import { Button } from '@klear/ui/components/button'
import { Calendar } from '@klear/ui/components/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'
import { UtcDateNotice } from '@klear/ui/components/utc-date-notice'
import type { KlearDate } from '@klear/date'
import { klearDateFromJsDate, klearDateToJsDate } from '@klear/date/boundary'

interface DatePickerProps {
  value?: KlearDate
  onChange?: (date: KlearDate | undefined) => void
  isEditing?: boolean
  disabled?: boolean
  size?: 'default' | 'sm' | 'xs'
}

export function DatePicker({ value, onChange, isEditing = true, disabled = false, size = 'default' }: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  const hasValue = value != null
  const label = value?.toString()
  const selectedJsDate = klearDateToJsDate(value)

  const emit = React.useCallback(
    (day: Date | undefined) => {
      onChange?.(klearDateFromJsDate(day))
    },
    [onChange]
  )

  React.useEffect(() => {
    if (disabled && open) {
      setOpen(false)
    }
  }, [disabled, open])

  return (
    <div className="min-w-8">
      <Popover
        open={open && isEditing && !disabled}
        onOpenChange={(newOpen) => isEditing && !disabled && setOpen(newOpen)}
      >
        <div className="relative min-w-8">
          <PopoverTrigger asChild>
            <Button
              variant={'outline'}
              size={size === 'sm' ? 'sm' : size === 'xs' ? 'xs' : 'default'}
              className={cn(
                'w-full justify-start text-left font-normal',
                !hasValue && 'text-muted-foreground',
                disabled && 'opacity-70 cursor-not-allowed',
                hasValue && isEditing && !disabled && 'pr-8'
              )}
              disabled={!isEditing || disabled}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {label ?? <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          {hasValue && isEditing && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 p-0 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                emit(undefined)
              }}
            >
              <XIcon className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            </Button>
          )}
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedJsDate}
            onSelect={(day) => {
              emit(day ?? undefined)
              setOpen(false)
            }}
            defaultMonth={selectedJsDate}
          />
        </PopoverContent>
      </Popover>
      {isEditing && <UtcDateNotice className="mt-1" />}
    </div>
  )
}
